import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs';
import { ProgressBarOverlayComponent } from 'src/app/components/progress-bar-overlay/progress-bar-overlay.component';
import { FileNode, FolderNode } from 'src/app/services/bucket/bucket.service';
import { ImportazioniService } from 'src/app/services/importazioni/importazioni.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

export interface EsitoDialogReplaceFile {
  isFolder: boolean;
  folder: FolderNode | undefined;
  file: File[];
}

@Component({
  selector: 'app-dialog-replace-file',
  standalone: false,
  templateUrl: './dialog-replace-file.component.html',
  styleUrl: './dialog-replace-file.component.scss',
})
export class DialogReplaceFileComponent {
  selectedOption: 'replace' | 'keep' = 'replace';
  @ViewChild(ProgressBarOverlayComponent) progressBarOverlay!: ProgressBarOverlayComponent;

  constructor(
    private readonly _importazioniService: ImportazioniService,
    private readonly _dialogRef: MatDialogRef<DialogReplaceFileComponent>,
    private readonly _utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      files: File[];
      fileNonCaricabiliIds: string[];
      parentPath: string;
      folder: FolderNode;
    },
  ) {}

  get isSingle(): boolean {
    return this.data.files.length === 1;
  }

  get fileName(): string {
    return this.data.files[0].name;
  }

  conferma() {
    switch (this.selectedOption) {
      case 'replace':
        this._replaceFile();
        break;
      case 'keep':
        this._keepFiles();
        break;
      default:
        break;
    }
  }

  /**
   * Esegue la sostituzione dei file selezionati.
   *
   * @private
   * @returns
   */
  private _replaceFile() {
    let msgLoad =
      this.data.files.length === 1
        ? `Sostituzione del file "${this.data.files[0].name}" in corso...`
        : `Sostituzione files in corso...`;

    this.progressBarOverlay.show(msgLoad);

    this._importazioniService
      .replaceFiles(this.data.files, this.data.fileNonCaricabiliIds, this.data.parentPath)
      .pipe(finalize(() => this.progressBarOverlay.hide()))
      .subscribe({
        next: (esito) => {
          if (esito?.body) {
            const esitoDiag: EsitoDialogReplaceFile = {
              isFolder: true,
              file: [],
              folder: esito.body,
            };

            this._dialogRef.close(esitoDiag);
          }
        },
        error: (error) => {
          console.error(error);

          this._utilityService.openSnackBar(
            'Si è verificato un errore durante la sostituzione dei file.',
            '',
            {
              duration: 5000,
              panelClass: ['red-snackbar'],
            },
          );
        },
      });
  }

  /**
   * Mi conserva entrambi i files, modificando il nome del file con l'incremento di 1.
   */
  private _keepFiles() {
    const files: File[] = this._getProgressiveFile(this.data.files, this.data.folder.files);

    const esitoDiag: EsitoDialogReplaceFile = {
      isFolder: false,
      file: files,
      folder: undefined,
    };

    this._dialogRef.close(esitoDiag);
  }

  /**
   * Genera nuovi file con nomi progressivi per evitare conflitti con file esistenti.
   *
   * Questa funzione prende una lista di file da caricare e una lista di file già esistenti.
   * Per ciascun file nuovo, se il nome è già presente tra i file esistenti, viene aggiunto
   * un contatore in formato "nome (numero).estensione" fino a ottenere un nome unico.
   * Se il file già ha un suffisso numerico, il contatore viene incrementato correttamente.
   *
   * @returns {File[]} - Nuovo array di file con nomi univoci progressivi.
   */
  private _getProgressiveFile(files: File[], existingFiles: FileNode[]): File[] {
    const existingNames = existingFiles.map((f) => f.name);

    return files.map((file) => {
      const filename = file.name;

      const lastDotIndex = filename.lastIndexOf('.');
      const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
      let baseName = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;

      // Regex per trovare " (numero)" alla fine
      const match = baseName.match(/^(.*)\s\((\d+)\)$/);

      let counter = 1;

      if (match) {
        baseName = match[1];
        counter = parseInt(match[2], 10) + 1;
      }

      let newName = `${baseName} (${counter})${extension}`;

      // Finché il nome esiste già, incrementa
      while (existingNames.includes(newName)) {
        counter++;
        newName = `${baseName} (${counter})${extension}`;
      }

      // Aggiungi il nuovo nome alla lista esistenti per evitare duplicati tra i file in upload
      existingNames.push(newName);

      return new File([file], newName, {
        type: file.type,
        lastModified: file.lastModified,
      });
    });
  }
}
